import { z } from 'zod';

export const GAME_ID = 'city-tour';

export const MINUTES_OPTIONS = [15, 20, 30] as const;
export const TURN_SECONDS_OPTIONS = [20, 30, 45] as const;
export const TEAM_COUNT_OPTIONS = [2, 3, 4] as const;
export const MAX_SOLO = 8;
export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 12;

/** Tahta animasyonu: sunucu sonucu bu süreler bitince açıklar. */
export const DICE_MS = 1100;
export const STEP_MS = 220;

/** Para TL cinsinden; ekranda "20 Mn" gibi görünür. */
export const START_MONEY = 20_000_000;
export const PASS_START = 3_000_000;
export const TOUR_FEE = 500_000;
export const BAIL = 2_000_000;
export const JAIL_TURNS = 3;
export const WIN_GROUPS = 3;
/** Anında kazanma bundan önce sayılmaz: oyun ilk turlarda bitmesin. */
export const MIN_WIN_LAPS = 3;
export const MAX_LEVEL = 4;
/** İlk turda en çok 2 ev; Başla'dan geçtikten sonra 3 ev; otel ancak 3 evli şehre yapılır. */
export const maxHouses = (laps: number): number => (laps >= 1 ? 3 : 2);
/** Kira katları: seviye 1 (1 ev) … 4 (otel). */
export const RENT_MULT = [0, 0.2, 0.5, 1.1, 2.4] as const;
export const UPGRADE_MULT = 0.6;
export const TAKEOVER_MULT = 2;
export const GROUP_RENT_MULT = 1.5;
export const FESTIVAL_MULT = 2;
export const FESTIVAL_COUNT = 2;
export const CUP_MAX = 8;
export const RESORT_PRICE = 2_000_000;
export const RESORT_RENT = 500_000;
export const RESORT_COUNT = 4;
export const CELL_COUNT = 32;

export const CUSTOM_CATEGORY = 'arkadas';
export const CATEGORY_NAMES: Record<string, string> = {
  hareket: 'Hareket',
  taklit: 'Taklit',
  dans: 'Dans',
  sarki: 'Şarkı',
  komik: 'Komik',
  cesaret: 'Cesaret',
  'cesur-yetiskin': 'Cesur (+18)',
  'ates-yetiskin': 'Ateşli (+18)',
  [CUSTOM_CATEGORY]: 'Arkadaş görevleri',
};
/** +18 paketler: varsayılan ayarlarda yoktur, oda sahibi bilerek açar. */
export const ADULT_CATEGORIES: readonly string[] = ['cesur-yetiskin', 'ates-yetiskin'];
export const isAdultCategory = (id: string): boolean => ADULT_CATEGORIES.includes(id);

export const settingsSchema = z.object({
  mode: z.enum(['solo', 'teams']),
  teamCount: z.number().int().min(2).max(4),
  /** Oyuncu kimlikleri; takımı seçilmemiş oyuncular başlarken en küçük takıma dağıtılır. */
  teams: z.array(z.array(z.string()).max(12)).max(4),
  minutes: z.number().int().min(1).max(30),
  turnSeconds: z.number().int().min(5).max(60),
  /** Kapalıysa şans kartları yalnızca oyun içi uygulanır. */
  realTasks: z.boolean(),
  categories: z.array(z.string().min(1).max(40)).max(30),
});
export type CityTourSettings = z.infer<typeof settingsSchema>;

export const defaultSettings: CityTourSettings = {
  mode: 'solo',
  teamCount: 2,
  teams: [],
  minutes: 20,
  turnSeconds: 30,
  realTasks: true,
  categories: ['hareket', 'taklit', 'dans', 'sarki', 'komik', 'cesaret', CUSTOM_CATEGORY],
};

export const customTaskSchema = z.object({
  text: z.string().trim().min(5, 'Görev en az 5 harf olmalı').max(120, 'Görev en fazla 120 harf olabilir'),
});

export interface CategoryInfo {
  id: string;
  name: string;
  count: number;
}

export const actionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('pick'), character: z.string() }),
  z.object({ type: z.literal('roll') }),
  z.object({ type: z.literal('bail') }),
  z.object({ type: z.literal('buy'), level: z.number().int().min(0).max(3) }),
  z.object({ type: z.literal('upgrade'), level: z.number().int().min(2).max(MAX_LEVEL) }),
  z.object({ type: z.literal('takeover') }),
  /** Karşısındaki teklifi geçer (satın alma, yükseltme, devralma). */
  z.object({ type: z.literal('pass') }),
  /** Dünya Turu: sıra gelince uçar. */
  z.object({ type: z.literal('tour'), cell: z.number().int().min(0).max(CELL_COUNT - 1) }),
  /** Şampiyona: kendi şehirlerinden birini seçer. */
  z.object({ type: z.literal('cup'), cell: z.number().int().min(0).max(CELL_COUNT - 1) }),
  z.object({ type: z.literal('applyCard') }),
  z.object({ type: z.literal('chooseTask') }),
  z.object({ type: z.literal('taskDone') }),
  z.object({ type: z.literal('taskQuit') }),
  z.object({ type: z.literal('vote'), done: z.boolean() }),
  z.object({ type: z.literal('sell'), cell: z.number().int().min(0).max(CELL_COUNT - 1) }),
  z.object({ type: z.literal('bankrupt') }),
]);
export type CityTourAction = z.infer<typeof actionSchema>;

// Tahta -----------------------------------------------------------------------------------------
export type CellKind = 'start' | 'island' | 'cup' | 'tour' | 'chance' | 'tax' | 'city' | 'resort';
export interface BoardCell {
  name: string;
  kind: CellKind;
  group?: number;
  price?: number;
}
/** Renk grupları, tahta sırasıyla. */
export const GROUP_COLORS = ['#ff8a5b', '#5ab8ff', '#ff7ac6', '#3ee0a8', '#ffd23f', '#b794ff', '#ff6b6b', '#8be04e'] as const;
const c = (name: string, group: number, price: number): BoardCell => ({ name, kind: 'city', group, price });
const resort = (name: string): BoardCell => ({ name, kind: 'resort', price: RESORT_PRICE });
const chance: BoardCell = { name: 'Şans', kind: 'chance' };
export const BOARD: readonly BoardCell[] = [
  { name: 'Başla', kind: 'start' },
  c('Kars', 0, 1_000_000), c('Van', 0, 1_200_000), chance, c('Rize', 1, 1_400_000), c('Ordu', 1, 1_600_000), c('Trabzon', 1, 1_800_000), resort('Kaş'),
  { name: 'Kayıp Ada', kind: 'island' },
  c('Konya', 2, 2_000_000), c('Sivas', 2, 2_200_000), resort('Uludağ'), c('Bursa', 3, 2_400_000), c('Eskişehir', 3, 2_600_000), c('Kayseri', 3, 2_800_000), chance,
  { name: 'Şampiyona', kind: 'cup' },
  c('Adana', 4, 3_000_000), c('Mersin', 4, 3_200_000), c('Hatay', 4, 3_400_000), resort('Çeşme'), c('Muğla', 5, 3_600_000), c('Denizli', 5, 3_800_000), { name: 'Vergi', kind: 'tax' },
  { name: 'Dünya Turu', kind: 'tour' },
  c('Antalya', 6, 4_000_000), c('Bodrum', 6, 4_200_000), resort('Ayvalık'), chance, c('İzmir', 7, 4_400_000), c('Ankara', 7, 4_700_000), c('Çorum', 7, 5_000_000),
];
export const JAIL_CELL = 8;
/** Kenarlar: köşeler hariç 7 kare. */
export const sideOf = (i: number): number => Math.floor(i / 8);
export const round100k = (n: number): number => Math.round(n / 100_000) * 100_000;
export const upgradeCost = (price: number): number => round100k(price * UPGRADE_MULT);
/** Şehre konan toplam para: satın alma + yükseltmeler. */
export const invested = (price: number, level: number): number => (level <= 0 ? 0 : price + (level - 1) * upgradeCost(price));
/** "20 Mn", "800 bin": bütün para metinleri. */
export function moneyText(n: number): string {
  const a = Math.abs(n);
  if (a < 1_000_000) return `${Math.round(n / 1000)} bin`;
  return `${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, '').replace('.', ',')} Mn`;
}
/** Oyuncu şeridi için kısa para: "17,6M". */
export function moneyShort(n: number): string {
  return `${(Math.max(0, n) / 1_000_000).toFixed(1).replace(/\.0$/, '').replace('.', ',')}M`;
}
export const baseRent = (price: number, level: number): number => round100k(price * RENT_MULT[level]!);

// Karakterler -----------------------------------------------------------------------------------
export const CHARACTERS = [
  { id: 'patron', name: 'Patron' },
  { id: 'amca', name: 'Kasketli amca' },
  { id: 'is-kadini', name: 'İş kadını' },
  { id: 'genc', name: 'Kapüşonlu genç' },
  { id: 'sporcu', name: 'Sporcu' },
  { id: 'asci', name: 'Aşçı' },
  { id: 'rockci', name: 'Rockçı kız' },
  { id: 'teyze', name: 'Teyze' },
] as const;
export type CharacterId = (typeof CHARACTERS)[number]['id'];
/** Piyonun ayağındaki disk ve şehir çatıları: oyuncu/takım rengi. */
export const UNIT_COLORS = ['#ffd23f', '#ff7ac6', '#5ab8ff', '#3ee0a8', '#ff8a5b', '#b794ff', '#ff6b6b', '#ffffff'] as const;

// Görünüm ---------------------------------------------------------------------------------------
export type CityTourPhase = 'pick' | 'play' | 'podium';
export type Stage = 'pick' | 'roll' | 'moving' | 'buy' | 'upgrade' | 'takeover' | 'tour' | 'cup' | 'chance' | 'task' | 'vote' | 'debt' | 'over';

export interface CellView extends BoardCell {
  i: number;
  /** Grup rengi; şehir değilse null. */
  color: string | null;
  owner: string | null;
  level: number;
  /** Şu anki kira (grup, festival ve şampiyona dahil); sahipsizse 0. */
  rent: number;
  festival: boolean;
  /** Şampiyona katı; 1 = yok. */
  cup: number;
  /** Seviye başına kira (katlar hariç); index = seviye. */
  rents: number[];
  upgrade: number;
  sell: number;
}
export interface UnitView {
  id: string;
  name: string;
  members: string[];
  character: CharacterId | null;
  color: string;
  money: number;
  wealth: number;
  at: number;
  jail: number;
  bankrupt: boolean;
  shield: boolean;
  groups: number;
  /** Başla'dan kaç kez geçti. */
  laps: number;
  /** Sırası gelince Dünya Turu ile uçabilir. */
  tourReady: boolean;
}
export interface MoveView {
  seq: number;
  unitId: string;
  from: number;
  /** Eksi ise geri gider. */
  steps: number;
  dice: [number, number] | null;
  /** Yürümeden uçar (Dünya Turu, Başla'ya git, ada). */
  teleport: boolean;
}
export interface CardView {
  id: string;
  text: string;
  good: boolean;
  /** Görev başarılı olursa ne olacağı. */
  reward: string;
}
export interface TaskView {
  text: string;
  category: string;
  seconds: number;
}
export type Prompt =
  | { kind: 'buy'; cell: number; options: { level: number; cost: number; rent: number; canAfford: boolean }[] }
  | { kind: 'resort'; cell: number; cost: number; canAfford: boolean }
  | { kind: 'upgrade'; cell: number; options: { level: number; cost: number; rent: number; canAfford: boolean }[]; locked: boolean }
  | { kind: 'takeover'; cell: number; cost: number; canAfford: boolean; from: string }
  | { kind: 'tour'; cost: number; canAfford: boolean; cells: number[] }
  | { kind: 'cup'; cells: { cell: number; rent: number; next: number }[] }
  | { kind: 'debt'; amount: number; need: number; to: string | null };
export interface PodiumRow {
  unitId: string;
  wealth: number;
  money: number;
  cities: number;
  bankrupt: boolean;
  rank: number;
}
export type EndReason = 'time' | 'last' | 'groups' | 'side' | 'resorts' | 'host';

export interface CityTourView {
  phase: CityTourPhase;
  stage: Stage;
  serverNow: number;
  /** Aşamanın bitişi; 0 = süresiz. */
  endsAt: number;
  durationMs: number;
  gameEndsAt: number;
  turnSeconds: number;
  cells: CellView[];
  units: UnitView[];
  activeUnitId: string | null;
  /** Şu an hamle yapacak oyuncu. */
  actorId: string | null;
  myUnitId: string | null;
  isActor: boolean;
  dice: [number, number] | null;
  move: MoveView | null;
  prompt: Prompt | null;
  card: CardView | null;
  task: TaskView | null;
  /** Bu kartta görev seçilebilir mi. */
  canTask: boolean;
  vote: { done: number; failed: number; pending: number; total: number; mine: boolean | null; canVote: boolean } | null;
  /** Son olaylar, yeniden eskiye. */
  feed: string[];
  characters: { id: CharacterId; name: string; takenBy: string | null }[];
  podium: { rows: PodiumRow[]; reason: EndReason; winners: string[] } | null;
}
